package org.example.project.registrierung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.datenschutz.DatenschutzTexte
import org.example.project.datenschutz.SignaturDatenschutzOverlay
import org.example.project.service.LoginService
import org.example.project.service.PasswordPolicy
import org.example.project.service.PasswordResetApi
import org.example.project.login.UnifiedLoginContent
import org.example.project.login.UnifiedLoginLayout
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.settings.PasswordRequirementHint
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event

private const val MOBILE_FAHRER_CANVAS = "mobile-fahrer-signatur-canvas"

@Suppress("UnsafeCastFromDynamic")
private fun driverApkSavedLoginEmail(): String =
    (js(
        """
        (function() {
            try {
                var bridge = window.LumaticaFahrerApp;
                if (!bridge || !bridge.getSavedLogin) return "";
                var data = JSON.parse(bridge.getSavedLogin() || "{}");
                return data.email || "";
            } catch (e) {
                return "";
            }
        })()
        """
    ) as? String).orEmpty()

@Suppress("UnsafeCastFromDynamic")
private fun driverApkSavedLoginPassword(): String =
    (js(
        """
        (function() {
            try {
                var bridge = window.LumaticaFahrerApp;
                if (!bridge || !bridge.getSavedLogin) return "";
                var data = JSON.parse(bridge.getSavedLogin() || "{}");
                return data.password || "";
            } catch (e) {
                return "";
            }
        })()
        """
    ) as? String).orEmpty()

private fun saveDriverApkLogin(email: String, password: String) {
    js(
        """
        (function(email, password) {
            try {
                var bridge = window.LumaticaFahrerApp;
                if (bridge && bridge.saveLogin) bridge.saveLogin(email, password);
            } catch (e) {}
        })
        """
    )(email, password)
}

@Composable
fun MobileFahrerRegistrierungScreen(
    onComplete: () -> Unit,
    externalScreen: String = "LOGIN"
) = MobileAccessScreen(onComplete, externalScreen, "mobile", allowRegistration = true)

@Composable
fun CompanionLoginScreen(
    onComplete: () -> Unit
) = MobileAccessScreen(onComplete, "LOGIN", "companion", allowRegistration = false)

@Composable
private fun MobileAccessScreen(
    onComplete: () -> Unit,
    externalScreen: String,
    loginMode: String,
    allowRegistration: Boolean
) {
    val scope = rememberCoroutineScope()

    var screenWidth by remember { mutableStateOf(window.innerWidth) }
    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { screenWidth = window.innerWidth }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    var activeView by remember { mutableStateOf("LOGIN") }

    var loginEmail by remember { mutableStateOf("") }
    var loginPw by remember { mutableStateOf("") }
    var loginEmailErr by remember { mutableStateOf<String?>(null) }
    var loginPwErr by remember { mutableStateOf<String?>(null) }
    var loginLoading by remember { mutableStateOf(false) }
    var resetLoading by remember { mutableStateOf(false) }
    var resetInfo by remember { mutableStateOf<String?>(null) }
    var zweiFaktorToken by remember { mutableStateOf("") }
    var zweiFaktorCode by remember { mutableStateOf("") }
    var zweiFaktorEinrichtung by remember { mutableStateOf(false) }
    var zweiFaktorQrCode by remember { mutableStateOf("") }
    var zweiFaktorTrustedDevice by remember { mutableStateOf(false) }
    var triedApkAutoLogin by remember { mutableStateOf(false) }

    var passwordChangeUser by remember { mutableStateOf<All?>(null) }
    var newPassword by remember { mutableStateOf("") }
    var repeatPassword by remember { mutableStateOf("") }
    var passwordChangeError by remember { mutableStateOf<String?>(null) }
    var passwordChangeLoading by remember { mutableStateOf(false) }

    var regStep by remember { mutableStateOf(1) }
    var fahrerAll by remember {
        mutableStateOf(
            All(
                position = "Fahrer",
                sprache = "de",
                aktiv = false,
                datenschutzAkzeptiert = true,
                zugangMobil = true,
                zugangDesktop = false,
                registrationMode = "",
                snapKeyConnected = false
            )
        )
    }
    var passwort by remember { mutableStateOf("") }
    var passwortWdh by remember { mutableStateOf("") }
    var regLoading by remember { mutableStateOf(false) }
    var regError by remember { mutableStateOf<String?>(null) }
    var loginSignaturUser by remember { mutableStateOf<All?>(null) }
    var loginSignaturLoading by remember { mutableStateOf(false) }
    var loginSignaturError by remember { mutableStateOf<String?>(null) }
    var snapKeyInput by remember { mutableStateOf("") }
    var snapKeyLoading by remember { mutableStateOf(false) }
    var snapKeyError by remember { mutableStateOf<String?>(null) }

    var signaturOverlayVisible by remember { mutableStateOf(false) }
    var signaturCanvasGezeichnet by remember { mutableStateOf(false) }
    var signaturDatenschutzAkzeptiert by remember { mutableStateOf(false) }
    var registeredCompanies by remember { mutableStateOf<List<All>>(emptyList()) }

    LaunchedEffect(Unit) {
        LocalizationManager.initialize(fahrerAll.sprache.ifBlank { "de" })
    }

    LaunchedEffect(Unit) {
        registeredCompanies = DesktopRegistrierungApi.registeredCompanies()
    }

    fun doLoginWith(rawEmail: String, rawPassword: String, persistApkLogin: Boolean) {
        val mail = rawEmail.lowercase()
        if (mail.isEmpty() || rawPassword.isEmpty() || loginLoading) return
        loginLoading = true
        loginEmailErr = null
        loginPwErr = null
        scope.launch {
            try {
                val loginResult = MobileFahrerRegistrierungApi.login(mail, rawPassword, loginMode)
                if (loginResult.error == "build_reload") return@launch
                val user = loginResult.user
                if (user != null) {
                    LocalizationManager.load(user.sprache.ifBlank { "de" })
                }
                when {
                    user == null -> {
                        val messageKey = when (loginResult.error) {
                            "email_invalid" -> "driver_error_invalid_email"
                            "email_unknown" -> "driver_email_unknown"
                            "password_invalid" -> "driver_password_wrong"
                            "rate_limited" -> "driver_login_rate_limited"
                            "user_blocked" -> "driver_access_blocked"
                            "mobile_access_denied" -> "driver_error_no_mobile_access"
                            "companion_access_denied" -> "driver_error_no_companion_access"
                            "company_blocked" -> "driver_company_blocked"
                            "company_not_active" -> "driver_company_not_active"
                            "network_error" -> "driver_login_network_error"
                            else -> "driver_login_server_error"
                        }
                        if (loginResult.error == "password_invalid") loginPwErr = LocalizationManager.get(messageKey)
                        else loginEmailErr = LocalizationManager.get(messageKey)
                    }
                    user.gesperrt == true -> loginPwErr = LocalizationManager.get("driver_access_blocked")
                    loginMode.equals("companion", ignoreCase = true) && !user.zugangCompanion ->
                        loginPwErr = LocalizationManager.get("driver_error_no_companion_access")
                    !loginMode.equals("companion", ignoreCase = true) && !user.zugangMobil ->
                        loginPwErr = LocalizationManager.get("driver_error_no_mobile_access")
                    user.zweiFaktorErforderlich -> {
                        LoginService.captureSession(user)
                        zweiFaktorToken = user.zweiFaktorToken
                        zweiFaktorEinrichtung = user.zweiFaktorEinrichtung
                        zweiFaktorQrCode = user.zweiFaktorQrCode
                        zweiFaktorCode = ""
                        zweiFaktorTrustedDevice = false
                    }
                    !user.passwortVerifiziert -> {
                        LoginService.captureSession(user)
                        passwordChangeUser = user
                        newPassword = ""
                        repeatPassword = ""
                        passwordChangeError = null
                    }
                    user.signatur.isBlank() || !user.datenschutzAkzeptiert -> {
                        LoginService.captureSession(user)
                        loginSignaturUser = user
                        loginSignaturError = null
                        signaturCanvasGezeichnet = false
                        signaturDatenschutzAkzeptiert = false
                        signaturOverlayVisible = true
                    }
                    else -> {
                        LoginService.captureSession(user)
                        if (persistApkLogin) saveDriverApkLogin(mail, rawPassword)
                        LocalizationManager.load(user.sprache.ifBlank { "de" })
                        LoginService.login(user)
                        onComplete()
                    }
                }
            } finally {
                loginLoading = false
            }
        }
    }

    fun doLogin() {
        doLoginWith(loginEmail, loginPw, persistApkLogin = true)
    }

    suspend fun continueDriverLogin(user: All) {
        when {
            !user.passwortVerifiziert -> {
                LoginService.captureSession(user)
                passwordChangeUser = user
                newPassword = ""
                repeatPassword = ""
                passwordChangeError = null
            }
            user.signatur.isBlank() || !user.datenschutzAkzeptiert -> {
                LoginService.captureSession(user)
                loginSignaturUser = user
                loginSignaturError = null
                signaturCanvasGezeichnet = false
                signaturDatenschutzAkzeptiert = false
                signaturOverlayVisible = true
            }
            else -> {
                LoginService.captureSession(user)
                saveDriverApkLogin(loginEmail.lowercase(), loginPw)
                LocalizationManager.load(user.sprache.ifBlank { "de" })
                LoginService.login(user)
                onComplete()
            }
        }
    }

    fun doZweiFaktor() {
        if (loginLoading) return
        if (zweiFaktorCode.length != 6) {
            loginPwErr = LocalizationManager.get("driver_2fa_enter_code")
            return
        }
        loginLoading = true
        loginPwErr = null
        scope.launch {
            try {
                val user = MobileFahrerRegistrierungApi.checkZweiFaktor(zweiFaktorToken, zweiFaktorCode, zweiFaktorTrustedDevice)
                if (user == null) {
                    loginPwErr = LocalizationManager.get("driver_2fa_code_invalid")
                    return@launch
                }
                if (user.zweiFaktorFehler) {
                    loginPwErr = if (user.zweiFaktorGesperrt) {
                        LocalizationManager.get("driver_2fa_access_locked")
                    } else {
                        LocalizationManager.get("driver_2fa_code_wrong").replace("{attempts}", user.zweiFaktorVerbleibendeVersuche)
                    }
                    return@launch
                }
                zweiFaktorToken = ""
                zweiFaktorCode = ""
                zweiFaktorQrCode = ""
                zweiFaktorEinrichtung = false
                zweiFaktorTrustedDevice = false
                continueDriverLogin(user)
            } finally {
                loginLoading = false
            }
        }
    }

    fun requestPasswordReset() {
        val mail = loginEmail.trim().lowercase()
        if (mail.isBlank() || resetLoading) {
            loginEmailErr = LocalizationManager.get("driver_error_invalid_email")
            return
        }
        resetLoading = true
        resetInfo = null
        loginEmailErr = null
        scope.launch {
            try {
                PasswordResetApi.request(
                    mail,
                    if (loginMode.equals("companion", ignoreCase = true)) "companion" else "driver",
                    fahrerAll.sprache.ifBlank { LocalizationManager.currentLangCode }
                )
                resetInfo = LocalizationManager.get("driver_password_reset_requested")
            } catch (_: Throwable) {
                resetInfo = LocalizationManager.get("driver_password_reset_failed")
            } finally {
                resetLoading = false
            }
        }
    }

    androidx.compose.runtime.LaunchedEffect(Unit) {
        if (triedApkAutoLogin || LoginService.isLoggedIn()) return@LaunchedEffect
        triedApkAutoLogin = true
        val savedEmail = driverApkSavedLoginEmail()
        val savedPassword = driverApkSavedLoginPassword()
        if (savedEmail.isBlank() || savedPassword.isBlank()) return@LaunchedEffect
        activeView = "LOGIN"
        loginEmail = savedEmail
        loginPw = savedPassword
        doLoginWith(savedEmail, savedPassword, persistApkLogin = false)
    }

    fun doPasswordChange() {
        val user = passwordChangeUser ?: run {
            passwordChangeError = LocalizationManager.get("driver_error_no_user_context")
            return
        }
        val qualityError = PasswordPolicy.error(newPassword)
        if (qualityError != null) {
            passwordChangeError = LocalizationManager.get("driver_password_requirement")
            return
        }
        if (newPassword != repeatPassword) {
            passwordChangeError = LocalizationManager.get("driver_error_password_not_identical")
            return
        }
        if (passwordChangeLoading) return

        passwordChangeLoading = true
        passwordChangeError = null
        scope.launch {
            try {
                val updated = MobileFahrerRegistrierungApi.changePassword(user.id, user.firmenId, newPassword, loginMode)
                if (updated == null) {
                    passwordChangeError = LocalizationManager.get("driver_save_failed")
                    return@launch
                }
                passwordChangeUser = null
                saveDriverApkLogin(updated.email.ifBlank { user.email }, newPassword)
                LocalizationManager.load(updated.sprache.ifBlank { "de" })
                LoginService.login(updated)
                if (updated.signatur.isBlank() || !updated.datenschutzAkzeptiert) {
                    loginSignaturUser = updated
                    loginSignaturError = null
                    signaturCanvasGezeichnet = false
                    signaturDatenschutzAkzeptiert = false
                    signaturOverlayVisible = true
                } else {
                    onComplete()
                }
            } catch (_: Throwable) {
                passwordChangeError = LocalizationManager.get("driver_save_failed")
            } finally {
                passwordChangeLoading = false
            }
        }
    }

    fun openSignaturOverlay() {
        regError = null
        loginSignaturUser = null
        loginSignaturError = null
        signaturCanvasGezeichnet = false
        signaturDatenschutzAkzeptiert = false
        signaturOverlayVisible = true
    }

    fun resetRegistrationState() {
        regStep = 1
        regError = null
        fahrerAll = All(
            position = "Fahrer",
            sprache = fahrerAll.sprache.ifBlank { "de" },
            aktiv = false,
            datenschutzAkzeptiert = true,
            zugangMobil = true,
            zugangDesktop = false,
            registrationMode = "",
            snapKeyConnected = false
        )
        passwort = ""
        passwortWdh = ""
        snapKeyInput = ""
        snapKeyLoading = false
        snapKeyError = null
    }

    fun confirmSnapKey() {
        if (snapKeyInput.isBlank() || snapKeyLoading) return
        snapKeyLoading = true
        snapKeyError = null
        scope.launch {
            try {
                val firma = MobileFahrerRegistrierungApi.previewDriverSnapKey(snapKeyInput.trim())
                if (firma == null || firma.firmenId.isBlank()) {
                    snapKeyError = LocalizationManager.get("driver_snapkey_not_found")
                    return@launch
                }
                fahrerAll = fahrerAll.copy(
                    firmenId = firma.firmenId,
                    firma = firma.firma,
                    kunde = firma.kunde.ifBlank { firma.firma },
                    land = firma.land,
                    plz = firma.plz,
                    strasse = firma.strasse,
                    hausnummer = firma.hausnummer,
                    ort = firma.ort,
                    snapKey = snapKeyInput.trim(),
                    registrationMode = "snapkey",
                    snapKeyConnected = true
                )
                regStep = 3
            } catch (_: Throwable) {
                snapKeyError = LocalizationManager.get("driver_snapkey_check_failed")
            } finally {
                snapKeyLoading = false
            }
        }
    }

    fun finalizeRegistration(signaturBase64: String) {
        if (regLoading) return
        if (fahrerAll.snapKey.isBlank() || !fahrerAll.snapKeyConnected) {
            regError = LocalizationManager.get("driver_check_snapkey_first")
            regStep = 2
            return
        }
        regLoading = true
        regError = null
        scope.launch {
            try {
                val result = MobileFahrerRegistrierungApi.registerDriver(fahrerAll, passwort, signaturBase64)
                when {
                    !result.ok && result.error == "snapkey_required" -> regError = LocalizationManager.get("driver_check_snapkey_first")
                    !result.ok && result.error == "snapkey_not_found" -> regError = LocalizationManager.get("driver_snapkey_not_found")
                    !result.ok && result.error == "email_exists" -> regError = LocalizationManager.get("driver_email_exists")
                    !result.ok && result.error == "driver_save_failed" -> regError = LocalizationManager.get("driver_driver_save_failed")
                    !result.ok && result.error == "invalid_company" -> regError = LocalizationManager.get("driver_company_load_failed")
                    !result.ok -> regError = LocalizationManager.get("driver_registration_save_failed")
                    result.user != null -> {
                        registeredCompanies = DesktopRegistrierungApi.registeredCompanies()
                        signaturOverlayVisible = false
                        saveDriverApkLogin(fahrerAll.email.lowercase(), passwort)
                        LocalizationManager.load(fahrerAll.sprache.ifBlank { "de" })
                        LoginService.login(result.user)
                        onComplete()
                    }
                    else -> regError = LocalizationManager.get("driver_registration_save_failed")
                }
            } catch (e: Exception) {
                regError = "${LocalizationManager.get("driver_error_prefix")}: ${e.message}"
            } finally {
                regLoading = false
            }
        }
    }

    fun finalizeLoginSignatur(signaturBase64: String) {
        val user = loginSignaturUser ?: return
        if (loginSignaturLoading) return
        loginSignaturLoading = true
        loginSignaturError = null
        scope.launch {
            try {
                val savedUser = MobileFahrerRegistrierungApi.saveSignatur(user.id, user.firmenId, signaturBase64)
                if (savedUser == null || savedUser.signatur.isBlank()) {
                    loginSignaturError = LocalizationManager.get("driver_signature_save_failed")
                    return@launch
                }
                signaturOverlayVisible = false
                loginSignaturUser = null
                LocalizationManager.load(savedUser.sprache.ifBlank { "de" })
                LoginService.login(savedUser)
                onComplete()
            } catch (_: Throwable) {
                loginSignaturError = LocalizationManager.get("driver_signature_save_failed")
            } finally {
                loginSignaturLoading = false
            }
        }
    }

    fun setRegistrationStep(step: Int) {
        regError = null
        regStep = step
    }

    fun setView(view: String) {
        if (view == "REGISTER" && !allowRegistration) return
        activeView = view
        when (view) {
            "LOGIN" -> {
                loginEmailErr = null
                loginPwErr = null
                zweiFaktorToken = ""
                zweiFaktorCode = ""
                passwordChangeUser = null
                passwordChangeError = null
                newPassword = ""
                repeatPassword = ""
            }
            "REGISTER" -> {
                resetRegistrationState()
            }
        }
    }

    LaunchedEffect(externalScreen) {
        if (externalScreen != activeView) setView(externalScreen)
    }

    val loginState = FahrerLoginFlowState(
        email = loginEmail,
        pw = loginPw,
        emailErr = loginEmailErr ?: resetInfo,
        pwErr = loginPwErr,
        loading = loginLoading || resetLoading
    )
    val regState = FahrerRegFlowState(
        step = regStep,
        registrationMode = fahrerAll.registrationMode,
        snapKey = fahrerAll.snapKey,
        snapKeyConnected = fahrerAll.snapKeyConnected,
        firma = fahrerAll.firma,
        strasse = fahrerAll.strasse,
        hausnummer = fahrerAll.hausnummer,
        plz = fahrerAll.plz,
        ort = fahrerAll.ort,
        vorname = fahrerAll.vorname,
        nachname = fahrerAll.nachname,
        email = fahrerAll.email,
        mobil = fahrerAll.mobil,
        passwort = passwort,
        passwortWdh = passwortWdh,
        loading = regLoading,
        error = regError,
        snapKeyError = snapKeyError,
        snapKeyLoading = snapKeyLoading,
        driverLanguage = fahrerAll.sprache.ifBlank { "de" }
    )
    val loginCallbacks = FahrerLoginCallbacks(
        onEmail = { loginEmail = it; loginEmailErr = null },
        onPw = { loginPw = it; loginPwErr = null },
        onSubmit = ::doLogin,
        onRegister = {
            setView("REGISTER")
        },
        onForgotPassword = ::requestPasswordReset
    )
    val regCallbacks = FahrerRegCallbacks(
        onStep = ::setRegistrationStep,
        onSnapKey = {
            snapKeyInput = it
            snapKeyError = null
            regError = null
            fahrerAll = fahrerAll.copy(
                snapKey = it,
                firmenId = "",
                firma = "",
                kunde = "",
                land = "",
                plz = "",
                strasse = "",
                hausnummer = "",
                ort = "",
                registrationMode = "",
                snapKeyConnected = false
            )
        },
        onConfirmSnapKey = ::confirmSnapKey,
        onFirma = { fahrerAll = fahrerAll.copy(firma = it) },
        onStrasse = { fahrerAll = fahrerAll.copy(strasse = it) },
        onHausnummer = { fahrerAll = fahrerAll.copy(hausnummer = it) },
        onPlz = { fahrerAll = fahrerAll.copy(plz = it) },
        onOrt = { fahrerAll = fahrerAll.copy(ort = it) },
        onVorname = { fahrerAll = fahrerAll.copy(vorname = it) },
        onNachname = { fahrerAll = fahrerAll.copy(nachname = it) },
        onEmail = { fahrerAll = fahrerAll.copy(email = it) },
        onMobil = { fahrerAll = fahrerAll.copy(mobil = it) },
        onPasswort = { passwort = it },
        onPasswortWdh = { passwortWdh = it },
        onLanguage = { code ->
            scope.launch { LocalizationManager.load(code) }
            fahrerAll = fahrerAll.copy(sprache = code)
        },
        onFinalize = ::openSignaturOverlay
    )

    val registrationSwitch: (() -> Unit)? = if (allowRegistration) ({ setView("REGISTER") }) else null
    if (activeView == "LOGIN") {
        org.example.project.utils.DashboardVerlaufHintergrund()
        UnifiedLoginLayout(
            isMobile = screenWidth < 600,
            accessory = {
                FahrerLanguageDropdown(
                    selectedLanguage = fahrerAll.sprache.ifBlank { "de" },
                    onLanguage = regCallbacks.onLanguage,
                    compact = true
                )
            }
        ) {
            UnifiedLoginContent(
                identifier = loginState.email,
                password = loginState.pw,
                identifierPlaceholder = LocalizationManager.get("driver_email"),
                loading = loginState.loginLoading,
                submitLabel = LocalizationManager.get("driver_login_button"),
                rememberLabel = LocalizationManager.get("driver_remember_me"),
                errorText = loginState.emailErr ?: loginState.pwErr,
                forgotLabel = LocalizationManager.get("driver_password_forgotten"),
                switchPrefix = if (allowRegistration) LocalizationManager.get("driver_new_here") else null,
                switchLabel = if (allowRegistration) LocalizationManager.get("driver_register_button") else null,
                onIdentifierChange = loginCallbacks.onEmail,
                onPasswordChange = loginCallbacks.onPw,
                onSubmit = loginCallbacks.onSubmit,
                onForgot = loginCallbacks.onForgotPassword,
                onSwitch = registrationSwitch
            )
        }
    } else if (screenWidth < 600) {
        MobilFahrerRegistrierungLayout(
            activeView = activeView,
            loginState = loginState,
            regState = regState,
            loginCallbacks = loginCallbacks,
            regCallbacks = regCallbacks,
            registeredCompanies = registeredCompanies,
            onSetView = ::setView,
            companionMode = loginMode == "companion"
        )
    } else {
        TabletFahrerRegistrierungLayout(
            activeView = activeView,
            loginState = loginState,
            regState = regState,
            loginCallbacks = loginCallbacks,
            regCallbacks = regCallbacks,
            registeredCompanies = registeredCompanies,
            onSetView = ::setView,
            companionMode = loginMode == "companion"
        )
    }

    if (passwordChangeUser != null) {
        DriverPasswordChangeOverlay(
            newPassword = newPassword,
            repeatPassword = repeatPassword,
            error = passwordChangeError,
            loading = passwordChangeLoading,
            onNewPassword = {
                newPassword = it
                passwordChangeError = null
            },
            onRepeatPassword = {
                repeatPassword = it
                passwordChangeError = null
            },
            onSubmit = ::doPasswordChange
        )
    }

    if (zweiFaktorToken.isNotBlank()) {
        DriverZweiFaktorOverlay(
            setup = zweiFaktorEinrichtung,
            qrCode = zweiFaktorQrCode,
            code = zweiFaktorCode,
            trustedDevice = zweiFaktorTrustedDevice,
            error = loginPwErr,
            loading = loginLoading,
            onCode = {
                zweiFaktorCode = it.filter { char -> char in '0'..'9' }.take(6)
                loginPwErr = null
            },
            onTrustedDevice = { zweiFaktorTrustedDevice = it },
            onSubmit = ::doZweiFaktor
        )
    }

    if (signaturOverlayVisible) {
        val isLoginSignatur = loginSignaturUser != null
        SignaturDatenschutzOverlay(
            canvasId = MOBILE_FAHRER_CANVAS,
            datenschutzText = DatenschutzTexte.NETZWERK,
            datenschutzLabel = LocalizationManager.get("driver_privacy_consent"),
            buttonLabel = if (regLoading || loginSignaturLoading) "..." else if (isLoginSignatur) LocalizationManager.get("driver_save_signature") else LocalizationManager.get("driver_registration_finish"),
            canvasHoehePx = 160,
            maxWidthPx = screenWidth - 40,
            overlayStyle = {},
            cardStyle = {},
            canvasGezeichnet = signaturCanvasGezeichnet,
            onCanvasGezeichnetChange = { signaturCanvasGezeichnet = it },
            datenschutzAkzeptiert = signaturDatenschutzAkzeptiert,
            onDatenschutzAkzeptiertChange = { signaturDatenschutzAkzeptiert = it },
            fehlerText = loginSignaturError ?: regError,
            onComplete = { signaturBase64 ->
                if (isLoginSignatur) finalizeLoginSignatur(signaturBase64) else finalizeRegistration(signaturBase64)
            }
        )
    }

}

@Composable
private fun DriverZweiFaktorOverlay(
    setup: Boolean,
    qrCode: String,
    code: String,
    trustedDevice: Boolean,
    error: String?,
    loading: Boolean,
    onCode: (String) -> Unit,
    onTrustedDevice: (Boolean) -> Unit,
    onSubmit: () -> Unit
) {
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            property("bottom", "0")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(20.px)
            property("box-sizing", "border-box")
            backgroundColor(DashboardTheme.BACKGROUND_APP)
            property("background", DashboardTheme.BACKGROUND_APP_GRADIENT)
            property("z-index", "1120")
        }
    }) {
        Div({
            style {
                width(100.percent)
                maxWidth(420.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                padding(22.px)
                property("box-sizing", "border-box")
                backgroundColor(DashboardTheme.SURFACE_DEEP)
                borderRadius(8.px)
            }
        }) {
            H2 { Text(LocalizationManager.get("driver_2fa_title")) }
            P {
                Text(if (setup) LocalizationManager.get("driver_2fa_setup_hint") else LocalizationManager.get("driver_2fa_code_hint"))
            }
            if (setup && qrCode.isNotBlank()) {
                Img(src = qrCode, attrs = {
                    style {
                        width(200.px)
                        height(200.px)
                        property("align-self", "center")
                    }
                })
            }
            Input(InputType.Text, attrs = {
                value(code)
                placeholder(LocalizationManager.get("driver_2fa_code_placeholder"))
                attr("inputmode", "numeric")
                attr("autocomplete", "one-time-code")
                onInput { onCode(it.value) }
                onKeyDown {
                    if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                        it.preventDefault()
                        if (!loading) onSubmit()
                    }
                }
                style {
                    width(100.percent)
                    height(44.px)
                    padding(0.px, 12.px)
                    property("box-sizing", "border-box")
                }
            })
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                }
            }) {
                LumaticaCheckbox(
                    checked = trustedDevice,
                    onChange = onTrustedDevice,
                    enabled = !loading,
                    fontSizePx = 15
                )
                Span { Text(LocalizationManager.get("driver_2fa_trust_device")) }
            }
            error?.let { P({ style { color(Color("#ff6b6b")) } }) { Text(it) } }
            LumaticaButton(
                label = if (loading) "..." else LocalizationManager.get("driver_2fa_check_code"),
                onClick = onSubmit,
                variant = LumaticaButtonVariant.Action,
                enabled = !loading,
                fillWidth = true,
                heightPx = 44
            )
        }
    }
}

@Composable
private fun DriverPasswordChangeOverlay(
    newPassword: String,
    repeatPassword: String,
    error: String?,
    loading: Boolean,
    onNewPassword: (String) -> Unit,
    onRepeatPassword: (String) -> Unit,
    onSubmit: () -> Unit
) {
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            property("bottom", "0")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(20.px)
            property("box-sizing", "border-box")
            backgroundColor(DashboardTheme.BACKGROUND_APP)
            property("background", DashboardTheme.BACKGROUND_APP_GRADIENT)
            property("z-index", "1100")
        }
    }) {
        Div({
            style {
                width(100.percent)
                maxWidth(420.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                padding(24.px)
                borderRadius(18.px)
                property("box-sizing", "border-box")
                property("border", "1px solid #2a2d34")
                property("box-shadow", "0 12px 48px rgba(0,0,0,0.45)")
                backgroundColor(DashboardTheme.SURFACE_DEEP)
                color(cssColor("white"))
            }
        }) {
            H3({
                style {
                    margin(0.px)
                    color(cssColor("white"))
                }
            }) { Text(LocalizationManager.get("driver_change_password")) }
            P({
                style {
                    margin(0.px)
                    fontSize(14.px)
                    opacity(0.8)
                    color(cssColor("#9aa0a6"))
                }
            }) { Text(LocalizationManager.get("driver_first_login_password_hint")) }
            Input(InputType.Password, attrs = {
                style { passwordFieldStyle() }
                placeholder(LocalizationManager.get("driver_new_password"))
                value(newPassword)
                onInput { onNewPassword(it.value) }
                onKeyDown {
                    if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                        it.preventDefault()
                        onSubmit()
                    }
                }
            })
            PasswordRequirementHint(newPassword, LocalizationManager.get("driver_password_requirement"))
            Input(InputType.Password, attrs = {
                style { passwordFieldStyle() }
                placeholder(LocalizationManager.get("driver_repeat_password"))
                value(repeatPassword)
                onInput { onRepeatPassword(it.value) }
                onKeyDown {
                    if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                        it.preventDefault()
                        onSubmit()
                    }
                }
            })
            error?.let { msg ->
                P({
                    style {
                        margin(0.px)
                        fontSize(12.px)
                        color(cssColor("#ff6b6b"))
                    }
                }) { Text(msg) }
            }
            Button(attrs = {
                style {
                    width(100.percent)
                    height(48.px)
                    borderRadius(8.px)
                    property("border", "1px solid rgba(255,255,255,0.18)")
                    backgroundColor(org.jetbrains.compose.web.css.Color.transparent)
                    color(cssColor("white"))
                    property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)")
                }
                onClick { onSubmit() }
            }) {
                Text(if (loading) "..." else LocalizationManager.get("driver_save_and_login"))
            }
        }
    }
}

private fun org.jetbrains.compose.web.css.StyleScope.passwordFieldStyle() {
    width(100.percent)
    height(46.px)
    padding(0.px, 14.px)
    property("box-sizing", "border-box")
    property("border", "1px solid rgba(255,255,255,0.18)")
    borderRadius(8.px)
    backgroundColor(org.jetbrains.compose.web.css.Color.transparent)
    color(cssColor("white"))
}

private fun cssColor(value: String) = Color(value)
